import { apiGet } from '@/lib/api';

export type PublicLegalDocumentCode = 'privacy_policy' | 'terms_of_service' | 'data_deletion';

export interface PublicLegalDocumentDto {
  code: PublicLegalDocumentCode;
  title: string;
  version: string;
  published_at: string;
  content_markdown: string;
}

export function fetchPublicLegalDocument(code: PublicLegalDocumentCode): Promise<PublicLegalDocumentDto> {
  return apiGet<PublicLegalDocumentDto>(`/api/public/legal/${code}`);
}

/**
 * Fuso fixo: a data é formatada no build (Node, em UTC) e de novo no navegador.
 * Sem fuso explícito, um documento publicado perto da meia-noite mostraria dias
 * diferentes nos dois, e o React descartaria o HTML gerado.
 */
export function formatLegalPublishedAt(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long', timeZone: 'America/Sao_Paulo' }).format(new Date(iso));
}

declare global {
  // Preenchido pelo build: no servidor antes de renderizar, no navegador por um
  // <script> gravado só no HTML da página legal correspondente.
  var __OPERAX_LEGAL__: Partial<Record<PublicLegalDocumentCode, PublicLegalDocumentDto>> | undefined;
}

/** Documento que já veio no HTML da página, se houver. */
export function initialLegalDocument(code: PublicLegalDocumentCode): PublicLegalDocumentDto | null {
  return globalThis.__OPERAX_LEGAL__?.[code] ?? null;
}

/**
 * A API às vezes devolve markdown com indentação comum em (quase) todas as linhas
 * (artefato de string multilinha). Em Markdown, 4+ espaços = code block,
 * o que deixa o texto monoespaçado e com `**` literais. Remove essa indentação.
 */
export function normalizeLegalMarkdown(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const paddedIndents = lines
    .filter((line) => line.trim().length > 0)
    .map((line) => line.match(/^[ \t]*/)?.[0].length ?? 0)
    .filter((n) => n >= 2);
  if (paddedIndents.length === 0) return markdown.trim();

  const common = Math.min(...paddedIndents);
  return lines
    .map((line) => {
      if (line.trim().length === 0) return '';
      const lead = line.match(/^[ \t]*/)?.[0].length ?? 0;
      return lead >= common ? line.slice(common) : line;
    })
    .join('\n')
    .trim();
}
